import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Job, JobUpsertParams } from '../../../types/job';
import { useAdminStore } from '../../../stores/adminStore';
import FullBlockingModal from '../../common/FullBlockingModal';
import EditJobForm from '../../jobs/EditJobForm';

const MIN_REASON_LENGTH = 10;

interface AdminEditJobModalProps {
  job: Job;
  onClose: () => void;
  onSuccess?: () => void;
}

const AdminEditJobModal: React.FC<AdminEditJobModalProps> = ({ job, onClose, onSuccess }) => {
  const { t } = useTranslation(['admin', 'jobs', 'common']);
  const { adminUpsertJob } = useAdminStore();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const reasonError =
    reason.trim().length > 0 && reason.trim().length < MIN_REASON_LENGTH
      ? t('jobModeration.reasonTooShort', {
          ns: 'admin',
          min: MIN_REASON_LENGTH,
        })
      : null;

  const handleUpdateJob = async (updatedParams: JobUpsertParams) => {
    if (reason.trim().length < MIN_REASON_LENGTH) {
      setError(
        t('jobModeration.reasonRequired', {
          ns: 'admin',
          min: MIN_REASON_LENGTH,
        })
      );
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await adminUpsertJob(job.id, updatedParams, reason);
      toast.success(t('jobModeration.editSuccess', { ns: 'admin' }));
      onSuccess?.();
      onClose();
    } catch (err) {
      console.error(err);
      setError(t('error.generic', { ns: 'common' }));
    } finally {
      setSaving(false);
    }
  };

  const handleFormSubmit = (updatedParams: JobUpsertParams) => handleUpdateJob(updatedParams);

  const jobForForm: Job = {
    ...job,
    required_services: job.required_services ?? [],
  };

  return (
    <FullBlockingModal innerClassName="md:max-w-[72rem]">
      <button
        className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-2xl md:top-0"
        onClick={onClose}
        aria-label={t('form_actions.close', { ns: 'common' })}
        type="button"
        disabled={saving}
      >
        &times;
      </button>
      <h2 className="text-2xl font-bold text-secondary mb-2">
        {t('jobModeration.editTitle', { ns: 'admin' })}
      </h2>
      <p className="text-sm text-gray-600 mb-4">
        {t('jobModeration.editSubtitle', { ns: 'admin' })}
      </p>

      <div className="mb-6">
        <label
          htmlFor="admin-edit-reason"
          className="block text-sm font-semibold text-gray-700 mb-1"
        >
          {t('jobModeration.reasonLabel', { ns: 'admin' })}
        </label>
        <textarea
          id="admin-edit-reason"
          value={reason}
          onChange={e => setReason(e.target.value)}
          rows={3}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          placeholder={t('jobModeration.reasonPlaceholder', { ns: 'admin' })}
          disabled={saving}
        />
        {reasonError && <p className="mt-1 text-sm text-red-600">{reasonError}</p>}
      </div>

      <EditJobForm
        job={jobForForm}
        onUpdateJob={handleFormSubmit}
        onCancelChange={onClose}
        onClose={onClose}
        error={error}
        submitButtonLabel={t('jobModeration.confirmEdit', { ns: 'admin' })}
        submittingButtonLabel={t('loading', { ns: 'common' })}
      />
    </FullBlockingModal>
  );
};

export default AdminEditJobModal;
